/**
 * Sidebar — navigation principale (responsive : drawer mobile + collapsed desktop).
 */
import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { colors, fonts, TAGLINE } from "../styles/tokens";
import useAuth from "../hooks/useAuth";
import usePresenceAlerts from "../hooks/usePresenceAlerts";
import usePlanningPending from "../hooks/usePlanningPending";
import useInscriptionsPending from "../hooks/useInscriptionsPending";
import Avatar from "./Avatar";
import Badge from "./Badge";
import Logo from "./Logo";
import NotificationsBell from "./NotificationsBell";

const ICONS = {
  dashboard:  "📊",
  annuaire:   "👥",
  cellules:   "🔵",
  benevoles:  "🤝",
  evenements: "📅",
  presences:  "✓",
  finances:   "💰",
  parametres: "⚙️",
  logout:     "↪",
  profile:    "👤",
};

/** Liens disponibles selon le rôle. */
function getMenuItems(isAdmin) {
  if (isAdmin) {
    return [
      { to: "/dashboard",  label: "Tableau de bord", icon: ICONS.dashboard },
      { to: "/annuaire",   label: "Annuaire",        icon: ICONS.annuaire, inscriptions: true },
      { to: "/cellules",   label: "Cellules",        icon: ICONS.cellules },
      { to: "/benevoles",  label: "Bénévoles",       icon: ICONS.benevoles, pending: true },
      { to: "/presences",  label: "Présences",       icon: ICONS.presences, alerts: true },
      { to: "/evenements", label: "Événements",      icon: ICONS.evenements },
      { to: "/finances",   label: "Finances",        icon: ICONS.finances },
      { to: "/parametres", label: "Paramètres",      icon: ICONS.parametres },
    ];
  }
  return [
    { to: "/profil",     label: "Mon profil",   icon: ICONS.profile },
    { to: "/evenements", label: "Événements",   icon: ICONS.evenements },
    { to: "/finances",   label: "Mes dons",     icon: ICONS.finances },
    { to: "/parametres", label: "Paramètres",   icon: ICONS.parametres },
  ];
}

export default function Sidebar() {
  const { user, isAdmin, logout, usingMock } = useAuth();
  const { total_critiques = 0, total_alertes = 0 } = usePresenceAlerts();
  const { count: pendingPlanning = 0 } = usePlanningPending();
  const { count: pendingInscriptions = 0 } = useInscriptionsPending();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 900);
    handler();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const items = getMenuItems(isAdmin);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // ─── Styles ───
  const sidebarStyle = {
    width: 260,
    minWidth: 260,
    height: "100vh",
    background: colors.surface,
    borderRight: `1px solid ${colors.border}`,
    display: "flex",
    flexDirection: "column",
    padding: "24px 16px",
    gap: "16px",
    fontFamily: fonts.body,
    position: isMobile ? "fixed" : "sticky",
    top: 0,
    left: isMobile ? (mobileOpen ? 0 : -280) : 0,
    zIndex: 250,
    transition: "left 250ms ease-out",
  };

  const linkStyle = ({ isActive }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "10px",
    color: isActive ? colors.gold : colors.text,
    background: isActive ? colors.goldDim : "transparent",
    border: `1px solid ${isActive ? colors.goldBorder : "transparent"}`,
    fontSize: "14px",
    fontWeight: isActive ? 600 : 500,
    textDecoration: "none",
    transition: "all 150ms",
  });

  return (
    <>
      {/* Bouton hamburger (mobile uniquement) */}
      {isMobile && !mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Ouvrir le menu"
          style={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 260,
            background: colors.card,
            color: colors.text,
            width: 44,
            height: 44,
            borderRadius: "10px",
            border: `1px solid ${colors.border}`,
            fontSize: "20px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          ☰
        </button>
      )}

      {/* Overlay mobile */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 240,
          }}
        />
      )}

      <aside style={sidebarStyle}>
        {/* Logo + tagline + cloche notifications (admin) */}
        <div style={{ padding: "0 6px 16px", borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
            <Logo size="sm" showName />
            {isAdmin && <NotificationsBell />}
          </div>
          <div
            style={{
              marginTop: "10px",
              fontSize: "11px",
              color: usingMock ? colors.gold : colors.textMuted,
              letterSpacing: "0.02em",
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {usingMock ? "MODE DÉMO" : TAGLINE}
          </div>
        </div>

        {/* Profil utilisateur — lien vers /profil */}
        {user && (
          <Link
            to="/profil"
            onClick={() => isMobile && setMobileOpen(false)}
            title="Mon profil"
            style={{
              display: "flex", alignItems: "center", gap: "12px",
              padding: "10px 12px", background: colors.card, borderRadius: "10px",
              border: `1px solid ${colors.border}`,
              textDecoration: "none",
              transition: "border-color 150ms",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.goldBorder; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.border; }}
          >
            <Avatar name={user.full_name} src={user.photo_url} size="md" bordered />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: colors.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user.titre ? `${user.titre} ${user.full_name}` : user.full_name}
              </div>
              <Badge variant={isAdmin ? "gold" : "info"} size="sm" dot>
                {user.role}
              </Badge>
            </div>
            <span style={{ color: colors.textMuted, fontSize: "16px" }}>→</span>
          </Link>
        )}

        {/* Liens */}
        <nav style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
          {items.map((it) => {
            // Badge alertes pour le lien Présences
            const showAlertBadge = it.alerts && (total_critiques > 0 || total_alertes > 0);
            const alertBadgeColor = total_critiques > 0 ? colors.rose : "#FB923C";
            const alertBadgeCount = total_critiques > 0 ? total_critiques : total_alertes;
            // Badge orange pour les bénévoles en attente
            const showPendingBadge = it.pending && pendingPlanning > 0;
            // Badge rouge pour les inscriptions fidèles en attente (Annuaire)
            const showInscriptionsBadge = it.inscriptions && pendingInscriptions > 0;
            const showAnyBadge = showAlertBadge || showPendingBadge || showInscriptionsBadge;
            let badgeColor = "#FB923C";
            let badgeCount = pendingPlanning;
            if (showAlertBadge) { badgeColor = alertBadgeColor; badgeCount = alertBadgeCount; }
            else if (showInscriptionsBadge) { badgeColor = colors.rose; badgeCount = pendingInscriptions; }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                style={linkStyle}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span style={{ fontSize: "18px" }}>{it.icon}</span>
                <span style={{ flex: 1 }}>{it.label}</span>
                {showAnyBadge && (
                  <span
                    style={{
                      minWidth: 22,
                      height: 22,
                      padding: "0 7px",
                      borderRadius: "999px",
                      background: badgeColor,
                      color: "#FFFFFF",
                      fontSize: "11px",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: showAlertBadge && total_critiques > 0 ? "pulse-gold 2s infinite" : "none",
                    }}
                    title={
                      showAlertBadge
                        ? (total_critiques > 0
                          ? `${total_critiques} alerte${total_critiques > 1 ? "s" : ""} critique${total_critiques > 1 ? "s" : ""}`
                          : `${total_alertes} membre${total_alertes > 1 ? "s" : ""} en alerte`)
                        : showInscriptionsBadge
                          ? `${pendingInscriptions} nouveau${pendingInscriptions > 1 ? "x" : ""} inscrit${pendingInscriptions > 1 ? "s" : ""} en attente`
                          : `${pendingPlanning} bénévole${pendingPlanning > 1 ? "s" : ""} en attente de réponse`
                    }
                  >
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Déconnexion */}
        <button
          onClick={handleLogout}
          style={{
            display: "flex", alignItems: "center", gap: "12px",
            padding: "12px 14px", borderRadius: "10px",
            color: colors.rose, background: "transparent",
            border: `1px solid ${colors.border}`, fontSize: "14px", fontWeight: 500,
            transition: "all 150ms", textAlign: "left",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(224,91,106,0.08)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ fontSize: "18px" }}>{ICONS.logout}</span>
          <span>Se déconnecter</span>
        </button>
      </aside>
    </>
  );
}
